import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { genPlayTraceId } from '@/utils/playTraceId'

const TIPS = [
  'Your choices shape the romance—who will win your heart?',
  'Every decision leads to a new twist in your story.',
  'Multiple endings await. Will you find true love?',
  'Swipe right or left? Your choices decide the romance.',
  'One story, countless paths. Where will your heart lead?',
  'Fall in love your way—pick your perfect partner.',
  'From first date to forever: you write the love story.',
  'Will you choose the charming stranger or the loyal friend?',
  'Every chapter holds secrets. Choose wisely.',
  'Your happy ending is just a choice away.',
  'Steamy scenes or sweet romance? You set the tone.',
  'No wrong answers, only different love stories.',
  'Replay to discover all the romantic possibilities.',
  'Love triangles, second chances, and hidden sparks.',
  'Flirt, tease, or play hard to get—it\'s up to you.',
  'Your choices change the chemistry with every character.',
  'Will you risk it all for passion or play it safe?',
  'Unlock secret scenes with the right decisions.',
  'Romance blooms in unexpected choices.',
  'The power is in your hands—write your own love saga.',
]

/** 引擎地址上不透传的路由参数 */
const EXCLUDED_QUERY_KEYS = ['coverUrl']

export interface UseGameFrameOptions {
  /** 追加到引擎地址上的额外参数（如试玩模式 TrialMode=demo） */
  extraQuery?: Record<string, string>
  /** 进度类消息以外的消息回调（来源已校验为本页 iframe） */
  onMessage?: (event: MessageEvent) => void
  /** 资源加载到 100% 时回调（loading 隐藏之前，用于埋点） */
  onLoaded?: () => void
}

/**
 * 游戏 iframe 嵌入公共逻辑：引擎地址拼接、playTraceId 补齐、loading 进度、tips 轮播、
 * postMessage 监听（unity_progress / addressables_progress）。
 * 业务消息（返回、支付桥、试玩结束等）通过 onMessage 交给调用方。
 */
export function useGameFrame(options: UseGameFrameOptions = {}) {
  const route = useRoute()
  const router = useRouter()

  // 初始化时把 playTraceId 落到路由上（落地页跳转已带则复用），
  // gameUrl 复制 route.query 即可带上，保证浏览器地址与游戏引擎 URL 一致
  if (!route.query.playTraceId)
    router.replace({ query: { ...route.query, playTraceId: genPlayTraceId() } })

  const gameUrl = computed(() => {
    const params = new URLSearchParams()
    Object.entries(route.query).forEach(([key, value]) => {
      if (!EXCLUDED_QUERY_KEYS.includes(key) && value)
        params.set(key, value as string)
    })
    Object.entries(options.extraQuery ?? {}).forEach(([key, value]) => {
      if (value)
        params.set(key, value)
    })
    return `/content/read/index.html?${params.toString()}`
  })

  const iframeRef = ref<HTMLIFrameElement | null>(null)
  const showLoading = ref(true)
  const percent = ref(0)
  const currentTip = ref('')

  let tipsTimer: ReturnType<typeof setInterval> | null = null
  let hideTimer: ReturnType<typeof setTimeout> | null = null

  function randomTip() {
    currentTip.value = TIPS[Math.floor(Math.random() * TIPS.length)]
  }

  function stopTips() {
    if (tipsTimer) {
      clearInterval(tipsTimer)
      tipsTimer = null
    }
  }

  function hideLoading() {
    percent.value = 100
    setTimeout(() => {
      showLoading.value = false
      stopTips()
    }, 300)
  }

  function onGameMessage(event: MessageEvent) {
    // 仅接受本页 iframe 发来的消息，阻断任意窗口 postMessage 触发支付下单/跳转
    if (event.source !== iframeRef.value?.contentWindow)
      return

    const { type, percent: p } = event.data ?? {}
    if (!type)
      return

    switch (type) {
      case 'unity_progress':
        percent.value = p ?? 0
        break
      case 'addressables_progress':
        percent.value = p ?? 50
        if (p >= 100) {
          options.onLoaded?.()
          hideTimer = setTimeout(hideLoading, 500)
        }
        break
      default:
        options.onMessage?.(event)
    }
  }

  onMounted(() => {
    randomTip()
    tipsTimer = setInterval(randomTip, 2000)
    window.addEventListener('message', onGameMessage)
  })

  onUnmounted(() => {
    stopTips()
    if (hideTimer)
      clearTimeout(hideTimer)
    window.removeEventListener('message', onGameMessage)
  })

  return {
    route,
    router,
    iframeRef,
    gameUrl,
    showLoading,
    percent,
    currentTip,
    hideLoading,
    stopTips,
  }
}
